import { useState, useCallback, useRef } from 'react';
import { hitTestAnnotation } from '../components/canvas/renderers/annotationRenderer';

/**
 * Hook for handling annotation tool interactions
 */
export const useAnnotationTool = ({
  annotations,
  addAnnotation,
  updateAnnotation,
  removeAnnotation,
  annotationSettings,
  selectedAnnotations,
  setSelectedAnnotations,
  scale,
  setScale,
  offset,
  setOffset,
  canvasRef,
}) => {
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingAnnotation, setDrawingAnnotation] = useState(null);
  const [pinchStart, setPinchStart] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [draggingGrip, setDraggingGrip] = useState(null); // { annotationId, pointIndex }
  const panStartRef = useRef({ x: 0, y: 0 });
  const currentAnnotationId = useRef(null);
  const lastClickTimeRef = useRef(0); // For detecting double-clicks manually

  // Convert screen coordinates to canvas coordinates
  const screenToCanvas = useCallback((clientX, clientY) => {
    const canvas = canvasRef?.current;
    if (!canvas) return { x: clientX, y: clientY };

    const rect = canvas.getBoundingClientRect();
    const x = (clientX - rect.left - offset.x) / scale;
    const y = (clientY - rect.top - offset.y) / scale;

    return { x, y };
  }, [canvasRef, scale, offset]);

  // Hit test grip points on selected annotations (clouds, arrows, rectangles, circles)
  const hitTestGrip = useCallback((pos) => {
    const gripRadius = 12 / scale; // Grip hit radius in world coordinates

    for (const annotationId of selectedAnnotations) {
      const annotation = annotations.find(a => a.id === annotationId);
      if (!annotation) continue;

      // Cloud grips (polygon vertices)
      if (annotation.type === 'cloud' && annotation.points && annotation.points.length >= 3) {
        for (let i = 0; i < annotation.points.length; i++) {
          const p = annotation.points[i];
          const dist = Math.sqrt(Math.pow(pos.x - p.x, 2) + Math.pow(pos.y - p.y, 2));
          if (dist < gripRadius) {
            return { annotationId, pointIndex: i, type: 'cloud' };
          }
        }
      }

      // Arrow grips (start and end)
      if (annotation.type === 'arrow') {
        const startDist = Math.sqrt(Math.pow(pos.x - annotation.start.x, 2) + Math.pow(pos.y - annotation.start.y, 2));
        if (startDist < gripRadius) {
          return { annotationId, pointIndex: 0, type: 'arrow' };
        }
        const endDist = Math.sqrt(Math.pow(pos.x - annotation.end.x, 2) + Math.pow(pos.y - annotation.end.y, 2));
        if (endDist < gripRadius) {
          return { annotationId, pointIndex: 1, type: 'arrow' };
        }
      }

      // Rectangle grips (4 corners)
      if (annotation.type === 'rectangle') {
        const corners = [
          { x: annotation.start.x, y: annotation.start.y },
          { x: annotation.end.x, y: annotation.start.y },
          { x: annotation.end.x, y: annotation.end.y },
          { x: annotation.start.x, y: annotation.end.y },
        ];
        for (let i = 0; i < corners.length; i++) {
          const dist = Math.sqrt(Math.pow(pos.x - corners[i].x, 2) + Math.pow(pos.y - corners[i].y, 2));
          if (dist < gripRadius) {
            return { annotationId, pointIndex: i, type: 'rectangle' };
          }
        }
      }

      // Circle grips (4 cardinal points on ellipse)
      if (annotation.type === 'circle') {
        const grips = [
          { x: annotation.center.x, y: annotation.center.y - annotation.radiusY }, // top
          { x: annotation.center.x + annotation.radiusX, y: annotation.center.y }, // right
          { x: annotation.center.x, y: annotation.center.y + annotation.radiusY }, // bottom
          { x: annotation.center.x - annotation.radiusX, y: annotation.center.y }, // left
        ];
        for (let i = 0; i < grips.length; i++) {
          const dist = Math.sqrt(Math.pow(pos.x - grips[i].x, 2) + Math.pow(pos.y - grips[i].y, 2));
          if (dist < gripRadius) {
            return { annotationId, pointIndex: i, type: 'circle' };
          }
        }
      }
    }

    return null;
  }, [selectedAnnotations, annotations, scale]);

  // Handle pointer down for annotation tools
  const handleAnnotationPointerDown = useCallback((e, tool) => {
    // Handle two-finger gestures for pinch-to-zoom and pan
    if (e.touches?.length === 2 && setScale && setOffset) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const dist = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
      const centerX = (touch1.clientX + touch2.clientX) / 2;
      const centerY = (touch1.clientY + touch2.clientY) / 2;

      setPinchStart({
        dist,
        scale,
        centerX,
        centerY,
        offsetX: offset.x,
        offsetY: offset.y,
      });
      setIsPanning(true);
      panStartRef.current = { x: centerX, y: centerY };
      return;
    }

    const pos = screenToCanvas(e.clientX, e.clientY);

    // If select tool, try to select an annotation and start dragging
    if (tool === 'annotation-select') {
      // First check if clicking on a grip of a selected annotation
      const gripHit = hitTestGrip(pos);
      if (gripHit) {
        setDraggingGrip(gripHit);
        e.preventDefault?.();
        return;
      }

      const hit = hitTestAnnotation(annotations, pos.x, pos.y, scale);
      if (hit) {
        // Check if clicking on already selected annotation - start drag
        if (selectedAnnotations.includes(hit.id)) {
          setIsDragging(true);
          setDragStart(pos);
        } else {
          setSelectedAnnotations([hit.id]);
          // Start drag immediately for newly selected annotation
          setIsDragging(true);
          setDragStart(pos);
        }
      } else {
        setSelectedAnnotations([]);
        setIsDragging(false);
        setDragStart(null);
      }
      return;
    }

    setIsDrawing(true);

    switch (tool) {
      case 'annotation-arrow':
        setDrawingAnnotation({
          type: 'arrow',
          start: pos,
          end: pos,
          color: annotationSettings.color,
          thickness: annotationSettings.thickness,
        });
        break;

      case 'annotation-freehand':
        setDrawingAnnotation({
          type: 'freehand',
          points: [pos],
          color: annotationSettings.color,
          thickness: annotationSettings.thickness,
        });
        break;

      case 'annotation-circle':
        setDrawingAnnotation({
          type: 'circle',
          center: pos,
          radiusX: 0,
          radiusY: 0,
          color: annotationSettings.color,
          thickness: annotationSettings.thickness,
        });
        break;

      case 'annotation-rectangle':
        setDrawingAnnotation({
          type: 'rectangle',
          start: pos,
          end: pos,
          color: annotationSettings.color,
          thickness: annotationSettings.thickness,
        });
        break;

      case 'annotation-cloud':
        // Detect double-click manually (within 400ms)
        const now = Date.now();
        const isDoubleClick = now - lastClickTimeRef.current < 400;
        lastClickTimeRef.current = now;

        // Cloud is drawn by clicking points - check if we're continuing an existing cloud
        if (drawingAnnotation && drawingAnnotation.type === 'cloud') {
          // Double-click to close (if we have 3+ points)
          if (isDoubleClick && drawingAnnotation.points.length >= 3) {
            const { previewPoint, ...cloudData } = drawingAnnotation;
            const id = addAnnotation({
              ...cloudData,
              label: annotationSettings.cloudLabel || '',
            });
            setSelectedAnnotations([id]);
            setDrawingAnnotation(null);
            setIsDrawing(false);
            return;
          }

          // Check if clicking near the first point to close the shape
          const firstPoint = drawingAnnotation.points[0];
          const dist = Math.sqrt(
            Math.pow(pos.x - firstPoint.x, 2) + Math.pow(pos.y - firstPoint.y, 2)
          );
          const closeThreshold = 20 / scale;

          if (drawingAnnotation.points.length >= 3 && dist < closeThreshold) {
            // Close the cloud - remove previewPoint before saving
            const { previewPoint, ...cloudData } = drawingAnnotation;
            const id = addAnnotation({
              ...cloudData,
              label: annotationSettings.cloudLabel || '',
            });
            setSelectedAnnotations([id]);
            setDrawingAnnotation(null);
            setIsDrawing(false);
          } else {
            // Add another point
            setDrawingAnnotation(prev => ({
              ...prev,
              points: [...prev.points, pos],
            }));
          }
        } else {
          // Start a new cloud - don't set isDrawing to true for click-based tools
          setDrawingAnnotation({
            type: 'cloud',
            points: [pos],
            previewPoint: pos,
            color: annotationSettings.color,
            thickness: annotationSettings.thickness,
          });
        }
        // Don't set isDrawing for cloud - it uses click-to-add-points not drag
        return;

      case 'annotation-stamp':
        // Stamps are placed immediately on click
        const stampId = addAnnotation({
          type: 'stamp',
          position: pos,
          stampType: annotationSettings.stampType || 'approved',
          customText: annotationSettings.customStampText || '',
          date: new Date().toISOString(),
          stampScale: 1,
          rotation: 0,
        });
        setSelectedAnnotations([stampId]);
        setIsDrawing(false);
        break;

      case 'annotation-callout':
        // Callouts are placed immediately on click
        // Auto-number based on existing callouts
        const existingCallouts = annotations.filter(a => a.type === 'callout');
        const nextNumber = existingCallouts.length + 1;
        const calloutId = addAnnotation({
          type: 'callout',
          position: pos,
          number: nextNumber,
          noteText: '',
        });
        setSelectedAnnotations([calloutId]);
        setIsDrawing(false);
        break;

      default:
        setIsDrawing(false);
    }
  }, [screenToCanvas, annotations, annotationSettings, addAnnotation, setSelectedAnnotations, selectedAnnotations, scale, offset, setScale, setOffset, drawingAnnotation, hitTestGrip]);

  // Handle pointer move for annotation tools
  const handleAnnotationPointerMove = useCallback((e, tool) => {
    // Handle pinch-to-zoom with two fingers
    if (e.touches?.length === 2 && pinchStart && setScale && setOffset) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const dist = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
      const centerX = (touch1.clientX + touch2.clientX) / 2;
      const centerY = (touch1.clientY + touch2.clientY) / 2;

      // Calculate new scale based on pinch distance change
      const scaleChange = dist / pinchStart.dist;
      const newScale = Math.max(0.1, Math.min(5, pinchStart.scale * scaleChange));

      // Calculate offset to zoom towards the INITIAL pinch center
      const canvas = canvasRef?.current;
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        const initialPinchCenterX = pinchStart.centerX - rect.left;
        const initialPinchCenterY = pinchStart.centerY - rect.top;

        const scaleRatio = newScale / pinchStart.scale;
        const newOffsetX = initialPinchCenterX - (initialPinchCenterX - pinchStart.offsetX) * scaleRatio;
        const newOffsetY = initialPinchCenterY - (initialPinchCenterY - pinchStart.offsetY) * scaleRatio;

        // Also handle two-finger pan
        const panDeltaX = centerX - pinchStart.centerX;
        const panDeltaY = centerY - pinchStart.centerY;

        setScale(newScale);
        setOffset({ x: newOffsetX + panDeltaX, y: newOffsetY + panDeltaY });
      }
      return;
    }

    // Handle dragging a grip point
    if (draggingGrip && tool === 'annotation-select') {
      const pos = screenToCanvas(e.clientX, e.clientY);
      const annotation = annotations.find(a => a.id === draggingGrip.annotationId);
      if (!annotation) return;

      const { pointIndex, type } = draggingGrip;

      if (type === 'cloud' && annotation.points) {
        // Update the specific vertex
        const newPoints = [...annotation.points];
        newPoints[pointIndex] = pos;
        updateAnnotation(draggingGrip.annotationId, { points: newPoints });
      } else if (type === 'arrow') {
        if (pointIndex === 0) {
          updateAnnotation(draggingGrip.annotationId, { start: pos });
        } else {
          updateAnnotation(draggingGrip.annotationId, { end: pos });
        }
      } else if (type === 'rectangle') {
        // Rectangle corners: 0=start, 1=topRight, 2=end, 3=bottomLeft
        const { start, end } = annotation;
        let newStart = { ...start };
        let newEnd = { ...end };

        switch (pointIndex) {
          case 0: // top-left (start)
            newStart = pos;
            break;
          case 1: // top-right
            newEnd.x = pos.x;
            newStart.y = pos.y;
            break;
          case 2: // bottom-right (end)
            newEnd = pos;
            break;
          case 3: // bottom-left
            newStart.x = pos.x;
            newEnd.y = pos.y;
            break;
        }
        updateAnnotation(draggingGrip.annotationId, { start: newStart, end: newEnd });
      } else if (type === 'circle') {
        // Circle grips: 0=top, 1=right, 2=bottom, 3=left
        const { center } = annotation;
        let newRadiusX = annotation.radiusX;
        let newRadiusY = annotation.radiusY;

        switch (pointIndex) {
          case 0: // top
          case 2: // bottom
            newRadiusY = Math.abs(pos.y - center.y);
            break;
          case 1: // right
          case 3: // left
            newRadiusX = Math.abs(pos.x - center.x);
            break;
        }
        updateAnnotation(draggingGrip.annotationId, { radiusX: newRadiusX, radiusY: newRadiusY });
      }
      return;
    }

    // Handle dragging selected annotations
    if (isDragging && dragStart && selectedAnnotations.length > 0 && tool === 'annotation-select') {
      const pos = screenToCanvas(e.clientX, e.clientY);
      const dx = pos.x - dragStart.x;
      const dy = pos.y - dragStart.y;

      // Update each selected annotation's position
      selectedAnnotations.forEach(id => {
        const annotation = annotations.find(a => a.id === id);
        if (!annotation) return;

        // Move annotation based on its type
        switch (annotation.type) {
          case 'arrow':
            updateAnnotation(id, {
              start: { x: annotation.start.x + dx, y: annotation.start.y + dy },
              end: { x: annotation.end.x + dx, y: annotation.end.y + dy },
            });
            break;
          case 'freehand':
            updateAnnotation(id, {
              points: annotation.points.map(p => ({ x: p.x + dx, y: p.y + dy })),
            });
            break;
          case 'circle':
            updateAnnotation(id, {
              center: { x: annotation.center.x + dx, y: annotation.center.y + dy },
            });
            break;
          case 'rectangle':
            updateAnnotation(id, {
              start: { x: annotation.start.x + dx, y: annotation.start.y + dy },
              end: { x: annotation.end.x + dx, y: annotation.end.y + dy },
            });
            break;
          case 'cloud':
            // Cloud can have points array (polygon) or start/end (legacy rectangle)
            if (annotation.points && annotation.points.length > 0) {
              updateAnnotation(id, {
                points: annotation.points.map(p => ({ x: p.x + dx, y: p.y + dy })),
              });
            } else if (annotation.start && annotation.end) {
              updateAnnotation(id, {
                start: { x: annotation.start.x + dx, y: annotation.start.y + dy },
                end: { x: annotation.end.x + dx, y: annotation.end.y + dy },
              });
            }
            break;
          case 'stamp':
          case 'callout':
            updateAnnotation(id, {
              position: { x: annotation.position.x + dx, y: annotation.position.y + dy },
            });
            break;
          default:
            break;
        }
      });

      setDragStart(pos);
      return;
    }

    // Handle cloud preview separately - it doesn't use isDrawing flag
    if (drawingAnnotation && drawingAnnotation.type === 'cloud' && tool === 'annotation-cloud') {
      const pos = screenToCanvas(e.clientX, e.clientY);
      setDrawingAnnotation(prev => ({
        ...prev,
        previewPoint: pos,
      }));
      return;
    }

    if (!isDrawing || !drawingAnnotation) return;

    const pos = screenToCanvas(e.clientX, e.clientY);

    switch (tool) {
      case 'annotation-arrow':
        setDrawingAnnotation(prev => ({
          ...prev,
          end: pos,
        }));
        break;

      case 'annotation-freehand':
        setDrawingAnnotation(prev => ({
          ...prev,
          points: [...prev.points, pos],
        }));
        break;

      case 'annotation-circle':
        const dx = pos.x - drawingAnnotation.center.x;
        const dy = pos.y - drawingAnnotation.center.y;
        setDrawingAnnotation(prev => ({
          ...prev,
          radiusX: Math.abs(dx),
          radiusY: Math.abs(dy),
        }));
        break;

      case 'annotation-rectangle':
        setDrawingAnnotation(prev => ({
          ...prev,
          end: pos,
        }));
        break;

      default:
        break;
    }
  }, [isDrawing, drawingAnnotation, screenToCanvas, pinchStart, setScale, setOffset, canvasRef, isDragging, dragStart, selectedAnnotations, annotations, updateAnnotation, draggingGrip]);

  // Handle pointer up for annotation tools
  const handleAnnotationPointerUp = useCallback((e, tool) => {
    // Clear pinch state
    if (pinchStart) {
      setPinchStart(null);
      setIsPanning(false);
    }

    // Clear grip drag state
    if (draggingGrip) {
      setDraggingGrip(null);
    }

    // Clear drag state
    if (isDragging) {
      setIsDragging(false);
      setDragStart(null);
    }

    if (!isDrawing || !drawingAnnotation) {
      setIsDrawing(false);
      return;
    }

    // Validate the annotation before adding
    let isValid = true;

    switch (tool) {
      case 'annotation-arrow':
        // Arrow needs minimum length
        const arrowLength = Math.sqrt(
          Math.pow(drawingAnnotation.end.x - drawingAnnotation.start.x, 2) +
          Math.pow(drawingAnnotation.end.y - drawingAnnotation.start.y, 2)
        );
        isValid = arrowLength > 10;
        break;

      case 'annotation-freehand':
        // Freehand needs minimum points
        isValid = drawingAnnotation.points.length > 2;
        break;

      case 'annotation-circle':
        // Circle needs minimum radius
        isValid = drawingAnnotation.radiusX > 5 || drawingAnnotation.radiusY > 5;
        break;

      case 'annotation-rectangle':
        // Rectangle needs minimum size
        const width = Math.abs(drawingAnnotation.end.x - drawingAnnotation.start.x);
        const height = Math.abs(drawingAnnotation.end.y - drawingAnnotation.start.y);
        isValid = width > 5 && height > 5;
        break;

      case 'annotation-cloud':
        // Cloud uses click-to-add-points - don't finalize on pointer up
        // The cloud is finalized when the user clicks near the first point or double-clicks
        return; // Exit early - don't clear drawing state

      default:
        break;
    }

    if (isValid) {
      const id = addAnnotation(drawingAnnotation);
      setSelectedAnnotations([id]);
    }

    setDrawingAnnotation(null);
    setIsDrawing(false);
  }, [isDrawing, drawingAnnotation, addAnnotation, setSelectedAnnotations, pinchStart, isDragging, draggingGrip]);

  // Handle double click for cloud tool (to close the shape)
  const handleAnnotationDoubleClick = useCallback((e, tool) => {
    if (tool === 'annotation-cloud' && drawingAnnotation && drawingAnnotation.type === 'cloud' && drawingAnnotation.points && drawingAnnotation.points.length >= 3) {
      // Remove previewPoint before saving
      const { previewPoint, ...cloudData } = drawingAnnotation;
      const id = addAnnotation({
        ...cloudData,
        label: annotationSettings.cloudLabel || '',
      });
      setSelectedAnnotations([id]);
      setDrawingAnnotation(null);
      setIsDrawing(false);
    }
  }, [drawingAnnotation, addAnnotation, setSelectedAnnotations, annotationSettings]);

  // Add point to cloud (on click) - legacy function, now handled in pointerDown
  const handleCloudClick = useCallback((e) => {
    if (!drawingAnnotation || drawingAnnotation.type !== 'cloud') return;

    const pos = screenToCanvas(e.clientX, e.clientY);
    setDrawingAnnotation(prev => ({
      ...prev,
      points: [...(prev.points || []), pos],
    }));
  }, [drawingAnnotation, screenToCanvas]);

  // Cancel cloud drawing (for Escape key)
  const cancelCloudDrawing = useCallback(() => {
    if (drawingAnnotation && drawingAnnotation.type === 'cloud') {
      setDrawingAnnotation(null);
      setIsDrawing(false);
    }
  }, [drawingAnnotation]);

  // Delete selected annotations
  const deleteSelectedAnnotations = useCallback(() => {
    selectedAnnotations.forEach(id => removeAnnotation(id));
    setSelectedAnnotations([]);
  }, [selectedAnnotations, removeAnnotation, setSelectedAnnotations]);

  // Resize selected stamp (scale up or down)
  const resizeSelectedStamp = useCallback((delta) => {
    if (selectedAnnotations.length !== 1) return;

    const annotation = annotations.find(a => a.id === selectedAnnotations[0]);
    if (!annotation || annotation.type !== 'stamp') return;

    const currentScale = annotation.stampScale || 1;
    const newScale = Math.max(0.5, Math.min(3, currentScale + delta));

    updateAnnotation(annotation.id, { stampScale: newScale });
  }, [selectedAnnotations, annotations, updateAnnotation]);

  return {
    isDrawing,
    drawingAnnotation,
    draggingGrip,
    handleAnnotationPointerDown,
    handleAnnotationPointerMove,
    handleAnnotationPointerUp,
    handleAnnotationDoubleClick,
    handleCloudClick,
    cancelCloudDrawing,
    deleteSelectedAnnotations,
    resizeSelectedStamp,
  };
};

export default useAnnotationTool;
